/* eslint-disable @sas/i18n/jsx-to-localize */
import { splitLocale } from '@sas/nova-core/i18n/localeUtils';
// Default Font Stacks
const AnovaUIFontFamily = `'Anova UI', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol'`;
const HelveticaNeueFontFamily = `Verdana, Helvetica, Arial, sans-serif`;
const MonospaceFontFamily = `Consolas, Menlo, "Lucida Console", Courier, Monaco, monospace`;

// Legacy Font Stacks
const LegacyAvenirNextFontFamily = `'AvenirNext', Arial, Helvetica, Verdana, 'Open Sans', sans-serif`;
const LegacyHelveticaNeueFontFamily = `HelveticaNeue, Helvetica, Arial, sans-serif`;
const LegacyNotoSansFontFamily = `'Noto Sans', sans-serif`;

/**
 * Converts a locale to the proper font stack.
 *
 * Documentation: http://livingston-snapshot.sas.com/11-0/essentials/fonts
 * Note: The above documentation is not entirely up to date.
 *
 * @param locale - the current application locale
 */
const getLocaleFontFamily = (locale, isLegacyTheme) => {
  // Split the locale into language, region, and script. Our font family designations contain different combinations of these aspects of the locale.
  const {
    lang,
    script,
    region
  } = splitLocale(locale);
  if (isLegacyTheme) {
    switch (lang) {
      case 'zh':
        if (region === 'TW' || script === 'Hant') {
          return `'Noto Sans TC', 'Noto Sans', 'PMingLiU', '新細明體', ${LegacyAvenirNextFontFamily}`;
        }
        if (region === 'HK') {
          return `'Noto Sans', 'Noto Sans TC', ${LegacyAvenirNextFontFamily}`;
        }
        // includes supported locales - zh, zh-cn-pseudo, zh-cn, zh-sg, zh-hans
        return `'Noto Sans SC', 'Noto Sans', 'SimSun', '宋体', ${LegacyAvenirNextFontFamily}`;
      case 'ja':
        return `'Meiryo UI',  'MS PGothic', 'Noto Sans JP', 'Noto Sans', 'ＭＳ Ｐゴシック', ${LegacyAvenirNextFontFamily}`;
      case 'ko':
        return `'Malgun Gothic', 'GulimChe', 'Gulim', 'Noto Sans KR', 'Noto Sans', '굴림', ${LegacyAvenirNextFontFamily}`;
      case 'th':
        return `'Noto Sans', 'Noto Sans Thai', ${LegacyAvenirNextFontFamily}`;
      case 'ar':
        // ar locale should follow regular font stack, but ar-xx uses PseudoArabic.
        if (region === 'XX') {
          return 'PseudoArabic, Helvetica, Arial, sans-serif';
        }
      default:
        return undefined;
    }
  }
  switch (lang) {
    case 'zh':
      if (region === 'TW' || script === 'Hant' || region === 'HK') {
        return `'Anova UI', '微軟正黑體', 'Microsoft JhengHei', '黑體-繁', 'Heiti TC', 'Helvetica Neue', 'Helvetica', Arial, sans-serif`;
      }
      // includes supported locales - zh, zh-cn-pseudo, zh-cn, zh-sg, zh-hans
      return `'Anova UI', '微软雅黑体', 'Microsoft YaHei New', '微软雅黑', 'Microsoft Yahei', '宋体', 'SimSun', 'Helvetica Neue', 'Helvetica', Arial, sans-serif`;
    case 'ja':
      return `'Anova UI', 'ヒラギノ角ゴ Pro W3', 'Hiragino Kaku Gothic Pro', 'Osaka', 'メイリオ', 'Meiryo', 'ＭＳ Ｐゴシック', 'MS PGothic', 'Noto Sans JP', 'Helvetica Neue', 'Helvetica', Arial, sans-serif`;
    case 'ko':
      return `'Anova UI', '맑은 고딕', 'Malgun Gothic', 'Apple SD Gothic Neo', 'NanumBarunGothic', '굴림', 'Gulim', '돋움', 'Dotum', 'Helvetica Neue', 'Helvetica', Arial, sans-serif`;
    case 'th':
      return `'Anova UI', 'Thonburi', 'Leelawadee UI', 'TH Sarabun New', 'Cordia New', Roboto, Noto, 'Noto Sans Thai', 'Helvetica Neue', 'Helvetica', Arial, sans-serif`;
    case 'vi':
    case 'he':
    case 'iw':
      return `'Anova UI', 'Helvetica Neue', 'Helvetica', Arial, sans-serif`;
    case 'ar':
      // ar locale should follow regular font stack, but ar-xx uses PseudoArabic.
      if (region === 'XX') {
        return `'Anova UI', 'PseudoArabic', 'Helvetica Neue', 'Helvetica', Arial, sans-serif`;
      }
      return `'Anova UI', 'Geeza Pro', 'Helvetica Neue', 'Helvetica', Arial, sans-serif`;
    default:
      return undefined;
  }
};

/**
 * Prepends a custom font family to a font stack.
 *
 * @param custom - custom font family
 * @param base - base font stack
 */
export const createFontStack = (custom, base) => {
  let baseStack = base;
  if (typeof base !== 'string') {
    baseStack = base.join(', ');
  }
  // Regex ensures that the custom value cannot be a string that contains empty single/double quotations.
  // Custom themes may contain these values for the value of fontFamilyOverride.  If the font stack ends
  // up with this value in it, it does not render properly in the global tokens provider.
  return `${custom && !/(?:""|'')/.test(custom) ? `'${custom}', ` : ''}${baseStack}`;
};
export const fontFamilyTokens = (theme, language) => {
  const customFamily = theme.overrides?.fontFamily;
  const isTheme2025Enabled = theme.baseReportThemeName === 'light2025' || theme.baseReportThemeName === 'dark2025' || theme.baseReportThemeName === 'highcontrast2020' || theme.enableNovaTokens;
  let defaultFontStack = createFontStack(customFamily, LegacyAvenirNextFontFamily);
  let itemFontStack = createFontStack(customFamily, LegacyAvenirNextFontFamily);
  let helveticaFontStack = createFontStack(customFamily, LegacyHelveticaNeueFontFamily);

  // High Contrast theme uses different font stack than other themes ('Noto Sans')
  if (theme.baseThemeName === 'high-contrast') {
    defaultFontStack = createFontStack(customFamily, LegacyNotoSansFontFamily);
    itemFontStack = createFontStack(customFamily, LegacyNotoSansFontFamily);
    helveticaFontStack = createFontStack(customFamily, LegacyNotoSansFontFamily);
  }
  if (isTheme2025Enabled) {
    defaultFontStack = createFontStack(customFamily, AnovaUIFontFamily);
    itemFontStack = createFontStack(customFamily, AnovaUIFontFamily);
    helveticaFontStack = createFontStack(customFamily, AnovaUIFontFamily);
  }

  // Certain locales use the 'Noto Sans' font stack
  const localeFontStack = getLocaleFontFamily(language.toLowerCase(), true);
  defaultFontStack = !!localeFontStack ? createFontStack(customFamily, localeFontStack) : defaultFontStack;
  return {
    default: defaultFontStack,
    secondary: defaultFontStack,
    monospace: MonospaceFontFamily,
    item: itemFontStack,
    helvetica: helveticaFontStack
  };
};
export const fontFamilyTokensNova = (theme, language) => {
  const customFamily = theme.overrides?.fontFamily;
  let defaultFontStack = createFontStack(customFamily, AnovaUIFontFamily);
  const itemFontStack = createFontStack(customFamily, AnovaUIFontFamily);
  const helveticaFontStack = createFontStack(customFamily, HelveticaNeueFontFamily);

  // Certain locales use the 'Noto Sans' font stack
  const localeFontStack = getLocaleFontFamily(language.toLowerCase(), false);
  defaultFontStack = !!localeFontStack ? createFontStack(customFamily, localeFontStack) : defaultFontStack;
  return {
    default: defaultFontStack,
    secondary: defaultFontStack,
    monospace: MonospaceFontFamily,
    item: itemFontStack,
    helvetica: helveticaFontStack
  };
};
//# sourceMappingURL=fontFamily.js.map